import { FiArrowRight, FiLoader } from "react-icons/fi";
import CustomButton from "../common/CustomButton";

export default function StripePayoutCard({
  connected = false,
  verifiedName = "",
  accountStatus = "",
  nextPayoutDate = "",
  payoutSchedule = "",
  onConnect,
  onUpdate,
  onDashboard,
  isConnecting = false,
  isDashboardLoading = false,
  isUpdateLoading = false,
  stripeDetails = {},
}) {
  return (
    <div className="w-full bg-white border border-[#E5E7EB] rounded-2xl p-4 sm:p-6 shadow-sm">
      {connected ? (
        /* ================= CONNECTED UI ================= */
        <div>
          {/* HEADER */}
          <div className="flex flex-col sm:flex-row items-start sm:items-start justify-between gap-4 sm:gap-0">
            <div className="flex items-start gap-3 sm:gap-4 flex-1 min-w-0">
              {/* Stripe Icon */}
              <div className="h-12 w-12 sm:h-14 sm:w-14 rounded-full bg-[#635BFF] flex items-center justify-center text-white text-sm sm:text-lg font-semibold flex-shrink-0">
                stripe
              </div>

              <div className="min-w-0 flex-1">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                  STRIPE ACCOUNT CONNECTED
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                  Your account is verified and ready to receive payouts.
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-col items-stretch sm:items-end gap-2 w-full sm:w-auto">
              <CustomButton
                onClick={onDashboard}
                className="w-full sm:w-auto"
                disabled={isDashboardLoading}
              >
                {isDashboardLoading ? (
                  <span className="flex items-center gap-2">
                    <FiLoader className="animate-spin" /> Loading...
                  </span>
                ) : (
                  "View Stripe Dashboard"
                )}
              </CustomButton>
            </div>
          </div>

          {/* DIVIDER */}
          <hr className="border-gray-200 my-4" />

          {/* DETAILS */}
          <div className="space-y-2 sm:space-y-0 sm:leading-[25px]">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1 sm:gap-2">
              <span className="font-semibold text-sm sm:text-base">
                Verified Name:
              </span>
              <span className="text-gray-900 text-sm sm:text-base break-words">
                {verifiedName}
              </span>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1 sm:gap-2">
              <span className="font-semibold text-sm sm:text-base">
                Account Status:
              </span>
              <span className="text-gray-900 text-sm sm:text-base">
                {accountStatus}
              </span>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1 sm:gap-2">
              <span className="font-semibold text-sm sm:text-base">
                Next Payout Date:
              </span>
              <span className="text-gray-900 text-sm sm:text-base">
                {nextPayoutDate}
              </span>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1 sm:gap-2">
              <span className="font-semibold text-sm sm:text-base">
                Next Payout Amount:
              </span>
              <span className="text-gray-900 text-sm sm:text-base">
                {stripeDetails?.nextPayoutAmount
                  ? `$${stripeDetails.nextPayoutAmount}`
                  : "No payout scheduled yet"}
              </span>
            </div>
          </div>
        </div>
      ) : (
        /* ================= UNCONNECTED UI ================= */
        <div>
          {/* HEADER */}
          <div className="flex flex-col sm:flex-row items-start sm:items-start justify-between gap-4 sm:gap-0">
            <div className="flex items-start gap-3 sm:gap-4 flex-1 min-w-0">
              {/* Stripe Icon */}
              <div className="h-12 w-12 sm:h-14 sm:w-14 rounded-full bg-[#635BFF] flex items-center justify-center text-white text-sm sm:text-lg font-semibold flex-shrink-0">
                stripe
              </div>

              <div className="min-w-0 flex-1">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                  STRIPE ACCOUNT CONNECT
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                  To receive payments connect your stripe account
                </p>
              </div>
            </div>

            {/* CONNECT BUTTON */}
            <CustomButton
              onClick={onConnect}
              disabled={isConnecting}
              className={`
                bg-[#01B9C6] hover:bg-[#00A8B4]
                text-white text-xs sm:text-sm font-medium
                py-2 sm:py-2.5 px-4 sm:px-6 rounded-md shadow-sm
                w-full sm:w-auto whitespace-nowrap
                ${isConnecting ? "opacity-70 cursor-not-allowed" : ""}
              `}
            >
              {isConnecting ? (
                <span className="flex items-center gap-2">
                  <FiLoader className="animate-spin" /> Connecting...
                </span>
              ) : (
                "Connect Your Stripe Account"
              )}
            </CustomButton>
          </div>

          {/* BULLETS */}
          <div className="mt-4 sm:mt-6 ml-0 sm:ml-16 text-xs sm:text-sm text-gray-700 space-y-2 sm:space-y-3">
            <Bullet text="Secure payouts" />
            <Bullet text="Automated deposits" />
            <Bullet text="Transaction history" />
            <Bullet text="Faster withdrawals" />
          </div>

          {/* STRIPE LOGO BOTTOM RIGHT */}
          <div className="flex justify-end mt-4 sm:mt-6 pr-0 sm:pr-4">
            <span className="text-2xl sm:text-4xl font-bold text-[#635BFF] opacity-70">
              stripe
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

/* ===== Helper Components ===== */

function Bullet({ text }) {
  return (
    <div className="flex items-center gap-2">
      <div className="h-2.5 w-2.5 rounded-full bg-[#00B8C8]" />
      <span>{text}</span>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <p className="text-sm text-gray-700">
      <span className="font-semibold">{label} :</span>{" "}
      <span className="text-gray-900">{value}</span>
    </p>
  );
}
